import { useEffect, type RefObject } from "react";

/**
 * Keep two scroll containers' vertical scroll positions in lockstep.
 * Used by the dep editor modal so the table and gantt strip never drift
 * apart row-wise.
 *
 * The echo event is suppressed by an equality check: assigning `scrollTop`
 * to the value it already has is a no-op, but the assignment still fires a
 * scroll event on some browsers — bailing when the values match breaks the
 * ping-pong regardless of when the echo fires.
 */
export function useSyncedScroll(
  refA: RefObject<HTMLElement | null>,
  refB: RefObject<HTMLElement | null>,
  enabled: boolean = true,
): void {
  useEffect(() => {
    if (!enabled) return;
    const a = refA.current;
    const b = refB.current;
    if (!a || !b) return;

    const mirror = (from: HTMLElement, to: HTMLElement) => () => {
      if (to.scrollTop === from.scrollTop) return;
      to.scrollTop = from.scrollTop;
    };

    const onA = mirror(a, b);
    const onB = mirror(b, a);
    a.addEventListener("scroll", onA, { passive: true });
    b.addEventListener("scroll", onB, { passive: true });
    return () => {
      a.removeEventListener("scroll", onA);
      b.removeEventListener("scroll", onB);
    };
  }, [refA, refB, enabled]);
}
